import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.map.js";
import { formatFileSize } from '@/utils';
import { api } from '@/server';
import { modal } from '@/share/components';
import { useAuthorStore } from '@/stores';
import { computed, reactive, ref } from 'vue';
import { apiHost, fileHost } from '@/server/urls';
import { VxeUI } from 'vxe-pc-ui';
export const useFileManager = ({
  params = {},
  paramName = 'file',
  maxSize = 10 * 1024 * 1024,
  // 10M
  allowExtensions = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
} = {}) => {
  const uploadError = ref('');
  const uploadOptions = reactive({
    maxSize: maxSize || 0,
    allowExtensions: allowExtensions || ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx']
  });
  const authorStore = useAuthorStore();
  const uploadFileTTLApi = getAPI(api.common.upload, {
    showError: false
  });
  const uploadFileApi = getAPI(api.common.uploadCommit, {
    showError: false
  });
  const {
    runAsync: delUploadTempFileApi
  } = fetchApi(api.common.delUpload);
  const {
    runAsync: delUploadFileApi
  } = fetchApi(api.common.delFile);
  const {
    runAsync: delFileByUrlApi
  } = fetchApi(api.common.delFileByUrl);
  const {
    runAsync: commitFilesApi
  } = fetchApi(api.common.commitFiles);
  const uploadHeaders = computed(() => {
    return {
      'x-access-token': authorStore.token
    };
  });
  const uploadParams = reactive({
    ...params
  });
  const uploadTTL = (file, updateProgress) => {
    return new Promise(resolve => {
      uploadError.value = '';
      const files = Array.isArray(file) ? file.map(item => {
        return {
          key: paramName || 'file',
          file: item,
          name: file.name
        };
      }) : [{
        key: paramName || 'file',
        name: file.name,
        file
      }];
      const names = files.map(item => item.name).join(',');
      uploadFileTTLApi({
        data: uploadParams,
        files: [...files],
        headers: uploadHeaders.value,
        onUploadProgress(event) {
          const percent = Math.round(event.loaded * 100 / (event.total || 100));
          !!updateProgress && updateProgress(percent);
        }
      }).then(res => {
        const {
          error,
          url,
          ...data
        } = res || {};
        if (error) {
          uploadError.value = error;
          modal.notification.error(`上传文件${names}出错:${error}`);
          resolve(false);
          return;
        }
        modal.notification.success(`上传文件${names}成功`);
        resolve({
          ...data,
          url: `${url ? apiHost + url : ''}`,
          status: 'success'
        });
      }).catch(err => {
        uploadError.value = err.message;
        modal.notification.error(`上传文件${names}出错:${err.message}`);
        resolve(false);
      });
    });
  };
  const upload = (file, {
    dir = '',
    cate = ''
  }, updateProgress = undefined) => {
    return new Promise(resolve => {
      uploadError.value = '';
      const files = Array.isArray(file) ? file.map(item => {
        return {
          key: paramName || 'file',
          file: item,
          name: file.name
        };
      }) : [{
        key: paramName || 'file',
        name: file.name,
        file
      }];
      const names = files.map(item => item.name).join(',');
      uploadFileApi({
        data: {
          ...uploadParams,
          dir,
          cate
        },
        files: [...files],
        headers: uploadHeaders.value,
        onUploadProgress(event) {
          const percent = Math.round(event.loaded * 100 / (event.total || 100));
          !!updateProgress && updateProgress(percent);
        }
      }).then(res => {
        const {
          error,
          url,
          ...data
        } = res || {};
        if (error) {
          uploadError.value = error;
          modal.notification.error(`上传文件${names}出错:${error}`);
          resolve(false);
          return;
        }
        modal.notification.success(`上传文件${names}成功`);
        if (!!data.rows && Array.isArray(data.rows)) {
          resolve([...data.rows.map(item => ({
            ...item,
            url: `${url ? apiHost + url : ''}`,
            status: 'success'
          }))]);
          return;
        }
        resolve({
          ...data,
          url: `${url ? apiHost + url : ''}`,
          status: 'success'
        });
      }).catch(err => {
        uploadError.value = err.message;
        modal.notification.error(`上传文件${names}出错:${err.message}`);
        resolve(false);
      });
    });
  };
  const uploadValidate = file => {
    const {
      allowExtensions,
      maxSize
    } = uploadOptions;
    const maxSizeName = formatFileSize(maxSize);
    const {
      name,
      size
    } = file || {};
    if (!name) return {
      status: 'error',
      error: '文件参数无效'
    };
    const ext = name.substr(name.lastIndexOf('.') + 1); // 获取文件后缀
    if (allowExtensions.length > 0 && allowExtensions.findIndex(t => t === ext) === -1) {
      return {
        status: 'error',
        error: `不支持上传${ext}格式的文件`
      };
    }
    // 文件大小限制
    if (maxSize > 0 && size > maxSize) {
      return {
        status: 'error',
        error: `文件大小不能超过${maxSizeName}`
      };
    }
    return {
      status: 'success',
      file
    };
  };
  const downloadFile = async file => {
    try {
      const {
        url
      } = file || {};
      let targetUrl;
      if (url.indexOf('?') === -1) {
        targetUrl = `${url}?action=download`;
      } else {
        targetUrl = `${url}&action=download`;
      }
      const blob = await fetch(targetUrl).then(response => response.blob());
      await VxeUI.saveFile({
        filename: file.name,
        content: blob
      });
      modal.notification.success(`下载文件${file.name}成功`);
      return Promise.resolve(true);
    } catch (e) {
      modal.notification.error(`下载文件${file.name}失败:${e.message}`);
      return Promise.resolve(false);
    }
  };
  const removeTmpFile = async file => {
    const {
      name,
      uid
    } = file || {};
    const {
      error
    } = await delUploadTempFileApi({
      filename: name,
      extra: uid
    });
    if (error) {
      modal.notification.error(error);
      return Promise.resolve(false);
    }
    modal.notification.success(`删除文件${name}成功`);
    return Promise.resolve(true);
  };
  const removeFile = async file => {
    const {
      name,
      fileId
    } = file || {};
    if (!fileId) return Promise.resolve(false);
    const res = await modal.confirm('删除确认', `确认要删除文件[${name}]吗?删除后不可恢复`);
    if (!res) return Promise.resolve(false);
    // 删除服务端文件
    const {
      error
    } = await delUploadFileApi({
      fileId
    });
    if (error) {
      modal.notification.error('删除文件', error);
      return Promise.resolve(false);
    }
    modal.notification.success(`删除文件[${file.name}]成功`);
    // 其他情况，直接删除
    return Promise.resolve(true); // 返回false表示不删除文件
  };

  // 清除文件主机名
  const clearFileUrlHost = (url = '') => {
    if (!url) return url;
    if (url.startsWith(fileHost)) {
      return url.replace(fileHost, '');
    }
    return url;
  };
  const removeFileByUrl = async url => {
    const postUrl = clearFileUrlHost(url);
    const {
      error
    } = await delFileByUrlApi({
      url: postUrl
    });
    if (error) {
      modal.notification.error(`删除文件${url}失败`);
      return Promise.resolve(false);
    }
    return Promise.resolve(true);
  };
  const commitUploadFiles = async ({
    dir = '',
    files = []
  }) => {
    const {
      error,
      ...res
    } = await commitFilesApi({
      dir,
      files
    });
    if (error) {
      modal.notification.error(error);
      return Promise.resolve({
        error
      });
    }
    return Promise.resolve(res);
  };

  // 选择并上传临时文件
  const chooseAndUploadTTL = async ({
    types = ['jpg', 'jpeg', 'png', 'gif', 'webp']
  }) => {
    return new Promise(resolve => {
      VxeUI.readFile({
        multiple: false,
        types: types || ['jpg', 'jpeg', 'png', 'gif', 'webp']
      }).then(chooseRes => {
        const {
          status: chooseStatus,
          file
        } = chooseRes || {};
        if (!chooseStatus) {
          modal.notification.info('取消了上传文件');
          return resolve(false);
        }
        const {
          status,
          error
        } = uploadValidate(file);
        if (status === 'error') {
          modal.notification.error(`文件${file.name}验证失败:${error}`);
          return resolve(false);
        }
        uploadTTL(file).then(resolve).catch(err => {
          modal.notification.error(`上传文件${file.name}出错:${err.message}`);
          resolve(false);
        });
      }).catch(err => {
        modal.notification.error(`读取文件失败:${err.message}`);
        resolve(false);
      });
    });
  };

  // 选择并上传文件
  const chooseAndUpload = async ({
    dir = '',
    cate = ''
  } = {}, {
    types = ['jpg', 'jpeg', 'png', 'gif', 'webp']
  } = {}) => {
    return new Promise(resolve => {
      VxeUI.readFile({
        multiple: false,
        types: types || ['jpg', 'jpeg', 'png', 'gif', 'webp']
      }).then(chooseRes => {
        const {
          status: chooseStatus,
          file
        } = chooseRes || {};
        console.log('===> chooseFile:', file);
        if (!chooseStatus) {
          modal.notification.info('取消了上传文件');
          return resolve(false);
        }
        const {
          status,
          error
        } = uploadValidate(file);
        if (status === 'error') {
          modal.notification.error(`文件${file.name}验证失败:${error}`);
          return resolve(false);
        }
        upload(file, {
          dir,
          cate
        }).then(resolve).catch(err => {
          modal.notification.error(`上传文件${file.name}出错:${err.message}`);
          resolve(false);
        });
      }).catch(err => {
        modal.notification.error(`读取文件失败:${err.message}`);
        resolve(false);
      });
    });
  };
  return {
    uploadError,
    uploadOptions,
    uploadHeaders,
    uploadParams,
    uploadValidate,
    downloadFile,
    upload,
    uploadTTL,
    chooseAndUploadTTL,
    chooseAndUpload,
    removeTmpFile,
    removeFile,
    removeFileByUrl,
    commitUploadFiles,
    clearFileUrlHost
  };
};